import React, { useRef, useState } from "react";
import { Callout, DirectionalHint } from "@fluentui/react/lib/Callout";
import { edcDateFormat } from "maven-lib/dist/consts/edc/lib";
import { classNames } from "maven-lib/dist/utils/misc";
import { CrInput, CrInputProps } from "./CrInput";
import { CrYearMonthExt } from "./CrYearMonthExt";
import dayjs from "dayjs";

export interface CrCalendarDateLabel {
  year?: string;
  month?: string;
}

export interface CrYearMonthInputProps extends Omit<CrInputProps, "defaultValue" | "value" | "iconName"> {
  minDate?: Date | number | "TODAY";
  maxDate: Date | number | "TODAY";
  allUnknownable: boolean;
  yearUnknownable: boolean;
  monthUnknownable: boolean;
  defaultValue?: string | Date;
  value?: string | Date;
  format: "YYYY/MM" | "YYYY-MM" | "MMM/YYYY";
  dateLabel?: CrCalendarDateLabel;
  onDeleteDate?: () => void;
}

export function CrYearMonthInput(props: CrYearMonthInputProps) {
  const dummyInputRef = useRef<HTMLInputElement>(null);
  const calendarInputRef = useRef<HTMLDivElement>(null);

  const {
    maxDate,
    minDate,
    defaultValue: defaultValueO,
    value: valueO,
    onValueChange,
    readonly,
    allUnknownable,
    yearUnknownable,
    monthUnknownable,
    format,
    onDeleteDate,
    dateLabel,
    ...extras
  } = props;
  const [showYearMonthInput, setShowYearMonthInput] = useState(false);

  const [controlledValue, setControlledValue] = useState<string>(() =>
    ((defaultValue, value) => {
      if (typeof value === "string") return edcDateFormat(value, format);

      if (typeof defaultValue === "string") return edcDateFormat(defaultValue, format);

      return "";
    })(defaultValueO, valueO)
  );

  const dismissYearMonthCallout = () => {
    setShowYearMonthInput(false);
    dummyInputRef.current?.focus();
  };

  const handleValueChange = (value: string) => {
    const v = (() => {
      if (format === "YYYY/MM") return `${value.slice(0, 4)}/${value.slice(4, 6)}`;
      else if (format === "YYYY-MM") return `${value.slice(0, 4)}-${value.slice(4, 6)}`;
      else if (format === "MMM/YYYY") {
        if (value.includes("UK") && value.slice(3, 8).includes("UK")) {
          return `UNK/${value.slice(0, 4)}`;
        }

        return `${dayjs()
          .month(Number(value.slice(4, 8)) - 1)
          .format("MMM")}/${value.slice(0, 4)}`;
      }

      // NOTE: 다른 format 추가됐을 때 대비해서 남겨 둠
      return `${value.slice(0, 4)}/${value.slice(4, 6)}`;
    })();

    onValueChange && onValueChange(v);
    setControlledValue(v);
    dismissYearMonthCallout();
  };

  const getSelectedYearMonth = (
    value: string,
    format: "YYYY/MM" | "YYYY-MM" | "MMM/YYYY"
  ): { year?: number; month?: number } | undefined => {
    if (!value) return undefined;

    switch (format) {
      case "MMM/YYYY": {
        const month = dayjs(value.slice(0, 3), "MMM").month() + 1;
        const year = value.slice(4);

        return {
          year: parseInt(year, 10) || undefined,
          month: month,
        };
      }
      default: {
        return {
          year: parseInt(value.slice(0, 4), 10) || undefined,
          month: parseInt(value.slice(5, 7), 10) || undefined,
        };
      }
    }
  };

  return (
    <div className={classNames("cr-calendar-input", showYearMonthInput && "is-active")}>
      <div ref={calendarInputRef}>
        <CrInput
          value={controlledValue}
          onFocus={() => !readonly && setShowYearMonthInput(true)}
          readonly={true}
          iconName="Calendar"
          {...extras}
        />
      </div>
      <input
        tabIndex={-1}
        ref={dummyInputRef}
        readOnly
        style={{ position: "absolute", top: "0", right: "0", width: "1px", height: "1px", opacity: 0.0001, zIndex: 0 }}
      />
      {showYearMonthInput && (
        <>
          <Callout
            isBeakVisible={true}
            className="ms-DatePicker-callout cr-calendar-callout"
            gapSpace={0}
            doNotLayer={false}
            target={calendarInputRef.current?.querySelector(".ms-TextField-fieldGroup")}
            directionalHint={DirectionalHint.bottomCenter}
            onDismiss={dismissYearMonthCallout}
          >
            <CrYearMonthExt
              maxDate={maxDate}
              minDate={minDate}
              selectedYearMonth={getSelectedYearMonth(controlledValue, format)}
              allUnknownable={allUnknownable}
              yearUnknownable={yearUnknownable}
              monthUnknownable={monthUnknownable}
              dateLabel={dateLabel}
              format={format}
              onDismiss={dismissYearMonthCallout}
              onValueChange={handleValueChange}
              onDeleteDate={() => {
                onDeleteDate && onDeleteDate();
                setControlledValue("");
                dismissYearMonthCallout();
              }}
            />
          </Callout>
        </>
      )}
    </div>
  );
}

CrYearMonthInput.defaultProps = {
  allUnknownable: false,
  yearUnknownable: false,
  monthUnknownable: false,
  maxDate: "TODAY",
};
